import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import PhoneChannelSettings from './channels/PhoneChannelSettings';
import RealtimeChannelSettings from './channels/RealtimeChannelSettings';
import WebChannelSettings from './channels/WebChannelSettings';
import useChannelSettings from './hooks/useChannelSettings';
import {
  ALL_CHANNEL_TYPES,
  CHANNEL_TYPE_ORDER,
  getChannelMeta,
} from './channelMeta';
import { API_BASE_URL } from '../../../../config/api';
import useIsMobile from '../../useIsMobile';
import '../../AssistantCard.css';
import '../AssistantSettingsTheme.css';

function ChannelSettingsPanel({ channelKey, assistant, saving, error, success, onSave }) {
  const initialSettings = assistant?.configs?.[channelKey] ?? {};
  const agentGreeting = assistant?.greeting || '';
  const props = {
    initialSettings,
    agentGreeting,
    agentId: assistant?.id,
    channelType: channelKey,
    onSave,
    saving: saving?.[channelKey],
    error: error?.[channelKey],
    success: success?.[channelKey],
  };

  switch (channelKey) {
    case 'WEB':
      return <WebChannelSettings {...props} />;
    case 'VAPI':
      return <PhoneChannelSettings {...props} />;
    case 'REALTIME':
      return <RealtimeChannelSettings {...props} />;
    default:
      return (
        <Typography color="text.secondary">
          No settings available for this channel type.
        </Typography>
      );
  }
}

const ChannelSettings = ({ assistant, onUpdate }) => {
  const { saving, error, success, handleSave } = useChannelSettings(assistant, onUpdate);
  const isMobile = useIsMobile();

  const [newChannelType, setNewChannelType] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [actionError, setActionError] = useState('');
  const [deleting, setDeleting] = useState({});
  const [expandedChannels, setExpandedChannels] = useState({});

  const connectedTypes = useMemo(() => {
    const configKeys = assistant?.configs
      ? Object.keys(assistant.configs).filter((key) => key !== 'BUSINESS')
      : [];

    const orderedKeys = CHANNEL_TYPE_ORDER.filter((key) => configKeys.includes(key));
    const remainingKeys = configKeys.filter((key) => !orderedKeys.includes(key));

    return [...orderedKeys, ...remainingKeys];
  }, [assistant?.configs]);

  const availableToAdd = useMemo(
    () => ALL_CHANNEL_TYPES.filter((channel) => !connectedTypes.includes(channel.value)),
    [connectedTypes]
  );

  const selectedChannelOption = useMemo(
    () => availableToAdd.find((channel) => channel.value === newChannelType) || null,
    [availableToAdd, newChannelType]
  );

  const handleToggleChannel = (channelType) => {
    setExpandedChannels((prev) => ({
      ...prev,
      [channelType]: !(prev[channelType] ?? false),
    }));
  };

  const handleAddChannel = async () => {
    if (!newChannelType) return;

    setAdding(true);
    setAddError('');
    setActionError('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/agents/${assistant.id}/channels/${newChannelType}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to add channel');
      }

      setNewChannelType('');
      setExpandedChannels((prev) => ({ ...prev, [newChannelType]: true }));

      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Add channel error:', err);
      setAddError(err.message || 'Failed to add channel');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteChannel = async (channelType) => {
    const meta = getChannelMeta(channelType);

    if (
      !window.confirm(
        `Delete the ${meta.label}? This will remove all its settings and cannot be undone.`
      )
    ) {
      return;
    }

    setDeleting((prev) => ({ ...prev, [channelType]: true }));
    setActionError('');

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/agents/${assistant.id}/channels/${channelType}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'ngrok-skip-browser-warning': 'true',
          },
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to delete channel');
      }

      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Delete channel error:', err);
      setActionError(err.message || `Failed to delete ${meta.label}.`);
    } finally {
      setDeleting((prev) => ({ ...prev, [channelType]: false }));
    }
  };

  return (
    <Box className="assistantCardSection">
      {actionError ? <Alert severity="error">{actionError}</Alert> : null}

      {availableToAdd.length > 0 ? (
        <Box className="assistantCardSubCard assistantChannelAddSurface">
          <Box className="assistantChannelAddRow">
            <Autocomplete
              fullWidth
              options={availableToAdd}
              value={selectedChannelOption}
              slotProps={{
                popper: {
                  className: 'assistantChannelAutocompletePopper',
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 12],
                      },
                    },
                  ],
                },
              }}
              onChange={(_event, option) => {
                setNewChannelType(option?.value || '');
                setAddError('');
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              noOptionsText="No more channels available"
              className="assistantChannelAutocomplete"
              renderOption={(props, option) => {
                const ChannelIcon = option.icon;

                return (
                  <Box component="li" {...props} className="assistantChannelOption">
                    <Box className={`assistantChannelOptionIcon assistantChannelIcon--${option.tone}`}>
                      <ChannelIcon fontSize="small" />
                    </Box>

                    <Box className="assistantChannelOptionCopy">
                      <Typography className="assistantChannelOptionTitle">
                        {option.label}
                      </Typography>
                      <Typography className="assistantChannelOptionDescription">
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add a channel"
                  placeholder="Search available channels"
                />
              )}
            />

            <Tooltip title={newChannelType ? 'Add selected channel' : 'Select a channel first'}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={handleAddChannel}
                  disabled={adding || !newChannelType}
                  className="assistantPrimaryButton assistantChannelAddAction"
                >
                  {adding ? <CircularProgress size={18} color="inherit" /> : 'Add'}
                </Button>
              </span>
            </Tooltip>
          </Box>

          {addError ? <Alert severity="error">{addError}</Alert> : null}
        </Box>
      ) : (
        <Box className="assistantCardSubCard assistantChannelAddSurface assistantChannelAddSurfaceMuted">
          <Typography className="assistantCardHelpText assistantCardHelpTextAlways" sx={{ mt: 0 }}>
            {isMobile ? 'All channels connected.' : 'All available channel types are already connected.'}
          </Typography>
        </Box>
      )}

      {connectedTypes.length === 0 ? (
        <Box className="assistantCardSubCard assistantChannelEmpty">
          <Box className="assistantChannelEmptyIcon">
            <HubOutlinedIcon fontSize="small" />
          </Box>
          <Typography className="assistantCardSubTitle">No channels connected</Typography>
          <Typography className="assistantCardHelpText assistantChannelEmptyCopy">
            {isMobile
              ? 'Add a channel to get started.'
              : 'Add a channel to configure web chat, phone calls, or realtime voice experiences for this assistant.'}
          </Typography>
        </Box>
      ) : (
        <Box className="assistantChannelStack">
          {connectedTypes.map((channelKey) => {
            const meta = getChannelMeta(channelKey);
            const ChannelIcon = meta.icon;
            const isExpanded = expandedChannels[channelKey] ?? false;

            return (
              <Box key={channelKey} className="assistantCardSubCard assistantChannelCard">
                <Box className="assistantChannelHeader">
                  <Box className="assistantChannelIdentity">
                    <Box className={`assistantChannelIcon assistantChannelIcon--${meta.tone}`}>
                      <ChannelIcon fontSize="small" />
                    </Box>

                    <Box className="assistantChannelCopy">
                      <Box className="assistantChannelTitleRow">
                        <Typography className="assistantCardSubTitle">
                          {meta.label}
                        </Typography>
                        <Chip size="small" label="Connected" className="assistantChannelChip" />
                      </Box>

                      <Typography className="assistantCardHelpText assistantChannelDescription">
                        {meta.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Box className="assistantChannelActions">
                    <Tooltip
                      title={isExpanded ? 'Collapse channel settings' : 'Expand channel settings'}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleToggleChannel(channelKey)}
                        className={`assistantCardExpand assistantChannelExpand ${
                          isExpanded ? 'expanded' : ''
                        }`}
                        aria-label={
                          isExpanded
                            ? `collapse ${meta.label.toLowerCase()} settings`
                            : `expand ${meta.label.toLowerCase()} settings`
                        }
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={`Delete ${meta.label}`}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteChannel(channelKey)}
                          disabled={Boolean(deleting[channelKey])}
                          className="assistantIconButtonDanger"
                          aria-label={`delete ${meta.label.toLowerCase()}`}
                        >
                          {deleting[channelKey] ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider className="assistantCardDivider" />
                  <ChannelSettingsPanel
                    channelKey={channelKey}
                    assistant={assistant}
                    saving={saving}
                    error={error}
                    success={success}
                    onSave={handleSave}
                  />
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default ChannelSettings;
